import Button from "./Button"
import TweetSubmissions from "./TweetSubmissions"
import { useForm, usePage } from "@inertiajs/react"
import { PromotionShowProps } from "@/types/types"
import useUser from "@/hooks/useUser"
import ErrorMessage from "./ErrorMessage"
import Container from "./Container"
import { router } from "@inertiajs/react"
import { useEffect } from "react"
import { ArrowUpTrayIcon } from "@heroicons/react/20/solid"
import {
  localeDetailFor,
  participationFlowFor,
  promotionCopyFor,
} from "@/utils/promotionLocale"

type Inputs = {
  url: string
}

const formatDateTime = (date: Date, locale: string) =>
  date.toLocaleString(localeDetailFor(locale).dateLocale, {
    dateStyle: "long",
    timeStyle: "short",
  })

const Tweets = ({ locale }: { locale: string }) => {
  const { promotion } = usePage<PromotionShowProps>().props
  const { isAuthenticated } = useUser()

  const copy = promotionCopyFor(locale)
  const participation = participationFlowFor(
    copy,
    promotion.auto_discovery_enabled
  )
  const participationSteps = [
    {
      title: copy.addGameStepTitle,
      body: copy.addGameStepBody,
      panelClassName: "bg-chessdrop-50/50",
      markerClassName: "bg-chessdrop-600",
    },
    {
      title: copy.shareStepTitle,
      body: copy.shareStepBody(promotion.hashtag),
      panelClassName: "bg-chessdrop-50",
      markerClassName: "bg-chessdrop-600",
    },
    {
      title: participation.entryStepTitle,
      body: participation.entryStepBody,
      panelClassName: "bg-emerald-50/70",
      markerClassName: "bg-emerald-600",
    },
  ]
  const { data, setData, post, errors, processing, recentlySuccessful, reset } =
    useForm<Inputs>({
      url: "",
    })

  const saveTweet = async (e: React.FormEvent) => {
    e.preventDefault()

    post(`/p/${promotion.slug}/tweets`, {
      async: true,
      preserveScroll: true,
      onSuccess: () => reset("url"),
    })
  }

  useEffect(() => {
    if (!promotion.auto_discovery_enabled || !promotion.active) return

    const refresh = window.setInterval(() => {
      if (document.visibilityState !== "visible") return

      router.reload({ only: ["promotion"] })
    }, 60_000)

    return () => window.clearInterval(refresh)
  }, [promotion.active, promotion.auto_discovery_enabled])

  return (
    <section className="border-b border-stone-200 bg-white py-10 sm:py-12">
      <Container maxWidthClassName="max-w-5xl">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold text-stone-950 sm:text-3xl">
            {copy.howToParticipate}
          </h2>

          <ol className="mt-5 grid overflow-hidden rounded-xl border border-stone-200 sm:grid-cols-3">
            {participationSteps.map(
              ({ title, body, panelClassName, markerClassName }, index) => (
                <li
                  key={title}
                  className={`${panelClassName} border-t border-stone-200 px-5 py-5 first:border-t-0 sm:border-t-0 sm:border-l sm:px-6 sm:first:border-l-0`}
                >
                  <span
                    aria-hidden="true"
                    className={`${markerClassName} flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white`}
                  >
                    {index + 1}
                  </span>
                  <h3 className="mt-4 text-base font-bold text-stone-950">
                    {title}
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-stone-600">
                    {body}
                  </p>
                </li>
              )
            )}
          </ol>

          <p className="mt-5 text-sm leading-6 text-stone-600 sm:text-base">
            {copy.submissionWindow(
              formatDateTime(new Date(promotion.start_at), locale),
              formatDateTime(new Date(promotion.end_at), locale)
            )}
          </p>
        </div>

        {promotion.active && (
          <div className="mt-6 rounded-lg border border-stone-200 bg-stone-50 p-4 sm:p-5">
            {isAuthenticated ? (
              <form onSubmit={saveTweet}>
                {recentlySuccessful && (
                  <p
                    role="status"
                    className="mb-3 text-sm font-semibold text-green-700"
                  >
                    {copy.submitted}
                  </p>
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label
                      htmlFor="url"
                      className="block text-sm leading-6 font-medium text-stone-900"
                    >
                      {copy.tweetUrl}
                    </label>
                    {promotion.auto_discovery_enabled && (
                      <p className="mt-1 text-sm leading-5 text-stone-500">
                        {copy.automaticDiscovery(
                          promotion.hashtag,
                          promotion.discovery_interval_minutes
                        )}{" "}
                        {copy.manualFallback}
                      </p>
                    )}
                    <input
                      type="url"
                      id="url"
                      placeholder="https://x.com/"
                      required
                      autoComplete="off"
                      className="focus:ring-chessdrop-600 mt-2 block h-11 w-full rounded-lg border-0 bg-white px-3 text-stone-900 shadow-xs ring-1 ring-stone-300 ring-inset placeholder:text-stone-400 focus:ring-2 focus:ring-inset"
                      value={data.url}
                      onChange={(e) => setData("url", e.target.value)}
                    />
                  </div>
                  <Button
                    type="submit"
                    loading={processing}
                    className="h-11 rounded-lg"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                    {copy.submitTweet}
                  </Button>
                </div>
                {errors.url ? (
                  <ErrorMessage className="mt-2" message={errors.url} />
                ) : null}
              </form>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-stone-700">
                  {copy.signInPrompt}
                </p>
                <Button
                  href={`/login?return_to=${encodeURIComponent(`/p/${promotion.slug}?lang=${locale}`)}`}
                  style="secondary"
                  size="sm"
                  className="rounded-lg"
                >
                  {copy.signIn}
                </Button>
              </div>
            )}
          </div>
        )}

        <TweetSubmissions locale={locale} />
      </Container>
    </section>
  )
}

export default Tweets
